import { loadModules } from 'esri-loader';
import env from '../config/environment';
import { removeDuplicates } from './data';

var _Extent, _Point, _watchUtils;
var _pointFeatureLayer, _lineFeatureLayer, _featureReduction;
var _definitionExpression, _mapPoint, _postMapSelection, _postMapZoom;
var _mapInteractionOccurred = false;
var _mapSelectionOccurred = false;

export function loadMap({mapElt, loadingElt, mapLoadErrorElt, initDefinitionExpression}) {
  return loadModules(['esri/Basemap', 'esri/Map', 'esri/views/MapView', 'esri/layers/FeatureLayer', 'esri/core/watchUtils', 'esri/geometry/SpatialReference', 'esri/geometry/Extent', 'esri/geometry/Point'], {
      css: true
    }).then(([Basemap, Map, MapView, FeatureLayer, watchUtils, SpatialReference, Extent, Point]) => {
      if (!mapElt) {
        console.log("Map element could not be loaded.");
        return;
      }

      mapLoadErrorElt.style.display = 'none';
      _Extent = Extent;
      _Point = Point;
      _watchUtils = watchUtils;

      const basemap = new Basemap({
        portalItem: {
          id: env.mapConfig.basemapId
        }
      });
      
      const map = new Map({
        basemap: basemap
      });

      let view = new MapView({
        map,
        container: mapElt,
        center: [239.5, 44],
        zoom: window.location.search.indexOf("map=show") !== -1 ? 5 : 6,
        constraints: {
          minZoom: 5
        }
      });
      
      _lineFeatureLayer = new FeatureLayer({
        url: env.agsrestapi + env.mapConfig.mapService + env.mapConfig.featureLayerIds[0],
        outFields: ["FME_PROJ_KEY_NO"],
        renderer: {
          type: "simple",
          symbol: {
            type: "simple-line",
            color: initDefinitionExpression ? "#FE5000" : "#001C8C",
            width: 3
          }
        }
      });
      _pointFeatureLayer = new FeatureLayer({
        url: env.agsrestapi + env.mapConfig.mapService + env.mapConfig.featureLayerIds[1],
        outFields: ["FME_PROJ_KEY_NO"],
        renderer: {
          type: "simple",
          symbol: {
            type: "simple-marker",
            color: initDefinitionExpression ? "#FE5000" : "#001C8C",
            size: 8,
            outline: initDefinitionExpression ? {
              color: "#FE5000",
              width: 1
            } : null
          }
        }
      });
      map.addMany([_lineFeatureLayer, _pointFeatureLayer]);

      _featureReduction = {
        type: "cluster",
        clusterRadius: "60px",
        clusterMinSize: "24px",
        clusterMaxSize: "40px",
        labelingInfo: [{
          deconflictionStrategy: "none",
          labelPlacement: "center-center",
        }]
      };

      view.spatialReference = new SpatialReference({
        wkid: 102100
      });
      
      if (!initDefinitionExpression) {
        map.layers.items[1].featureReduction = _featureReduction;
        view.ui.add("fullscreen", "manual");
        view.ui.add("removeMapSelection", "manual");
      }

      watchUtils.whenTrue(view, "updating", function(e) {
        loadingElt.style.display = 'block';
      });
      
      watchUtils.whenFalse(view, "updating", function(e) {
        loadingElt.style.display = 'none';
        if (!initDefinitionExpression && !_mapSelectionOccurred) {
          if (view.zoom >= 11) {
            map.layers.items[1].featureReduction = null;
          } 
          else {
            map.layers.items[1].featureReduction = _featureReduction;
          }
        }
      });

      return {map, view};
    }).catch(e => {
      console.log("Error occurred loading Esri modules: ", e);
      mapLoadErrorElt.style.display = 'block';
    });
}

export function initLayerViews(map, view) {
  let promises = [];
  for (let i = 0; i < map.layers.items.length; i++) {
    promises.push(view.whenLayerView(map.layers.items[i]));
  }

  const layerViews = Promise.all(promises);

  return layerViews;
}

export function initMapClick(map, view, layerViews, postMapSelection, noMapResultsElt) {
  _postMapSelection = postMapSelection;

  view.on("click", function(event) {
    let mapPointCoords = {
      lat: event.mapPoint.latitude,
      long: event.mapPoint.longitude,
      x: event.mapPoint.x,
      y: event.mapPoint.y,
      zoom: view.zoom
    }
    executeMapSelection(map, _definitionExpression, mapPointCoords, "addedMapPoint", view, layerViews, noMapResultsElt, _mapPoint, null);
  });
  
  _watchUtils.whenTrue(view, "stationary", () => {
    if (_mapInteractionOccurred) {
      _mapInteractionOccurred = false;
      updateProjectsList(view, layerViews);
    }
  });

  let zoomElements = document.getElementsByClassName("esri-zoom");
  let handleZoomClick = function() {
    _mapInteractionOccurred = true;
  };
  for (let i = 0; i < zoomElements.length; i++) {
    zoomElements[i].addEventListener('click', handleZoomClick);
  }

  view.on(["mouse-wheel", "drag"], function(event) {
    _mapInteractionOccurred = true;
  });
}

export function executeMapSelection(map, definitionExpression, mapPointCoords, updatedFilter, view, layerViews, noMapResultsElt, mapPoint, newExtent) {
  let queries = [];
  let extents = [];
  let layerViewFilter = {};
  let prevFilters = [layerViews[0].filter, layerViews[1].filter];
  let layers = view.map.layers.items;
  let initDefExp = layers[0].definitionExpression;
  let queryParams = {
    geometry: mapPointCoords ? 
      new _Point({
        hasM: false,
        hasZ: false,
        latitude: mapPointCoords.lat,
        longitude: mapPointCoords.long,
        m: undefined,
        spatialReference: view.spatialReference,
        type: "point",
        x: mapPointCoords.x,
        y: mapPointCoords.y,
        z: undefined
      }) : 
      newExtent ? newExtent : 
      new _Point({
        latitude: 44.15462243077733,
        longitude: -120.71777343749531,
        spatialReference: view.spatialReference,
        type: "point",
        x: -13438241.068759745,
        y: 5489401.623329771
      }),
    distance: newExtent ? 0 : (mapPointCoords ? (mapPointCoords.zoom < 8 ? 12 : mapPointCoords.zoom >= 8 && mapPointCoords.zoom < 10 ? 2 : mapPointCoords.zoom > 12 ? 0.005 : 0.5) : 1000),
    units: "miles",
    returnQueryGeometry: true,
    outFields: ["FME_PROJ_KEY_NO"],
    where: definitionExpression ? definitionExpression : null
  };
  
  for (let i = 0; i < layers.length; i++) {
    if (updatedFilter !== "") {
      layerViewFilter = definitionExpression ? {
        where: definitionExpression,
        geometry: queryParams.geometry,
        distance: queryParams.distance,
        units: queryParams.units
      } : {
        where: null,
        geometry: queryParams.geometry,
        distance: queryParams.distance,
        units: queryParams.units
      };
      
      if (mapPointCoords) {
        layerViews[i].filter = null;
        layerViews[i].effect = {
          filter: layerViewFilter,
          excludedEffect: "grayscale(100%) opacity(20%)"
        };
      }
      else {
        layerViews[i].effect = null;
        layerViews[i].filter = layerViewFilter;
      }
    }

    let query = layers[i].createQuery();
    query.geometry = queryParams.geometry;
    query.distance = queryParams.distance;
    query.units = queryParams.units;
    query.returnGeometry = queryParams.returnQueryGeometry;
    query.outFields = queryParams.outFields;
    query.where = queryParams.where;
    queries.push(query);
  }

  if (updatedFilter !== "" || mapPointCoords || definitionExpression) {
    layers[0].queryExtent(queries[0]).then(res => {
      if (res.extent && res.count > 0) {
        extents.push(res.extent);
      }

      layers[1].queryExtent(queries[1]).then(res2 => {
        if (res2.extent && res2.count > 0) {
          extents.push(res2.extent);
        }
        
        if (extents.length) {
          if (updatedFilter === "addedMapPoint" || updatedFilter === "urlAddedMapPoint" || mapPointCoords) {
            map.layers.items[1].featureReduction = null;
          }

          noMapResultsElt.style.display = 'none';
          let combinedExtent = new _Extent(extents[0].xmin, extents[0].ymin, extents[0].xmax, extents[0].ymax, extents[0].spatialReference);
          for (let i = 1; i < extents.length; i++) {
            combinedExtent = combinedExtent.union(new _Extent(extents[i].xmin, extents[i].ymin, extents[i].xmax, extents[i].ymax, extents[i].spatialReference));
          }

          if (combinedExtent.xmax === combinedExtent.xmin && combinedExtent.ymax === combinedExtent.ymin) {
            combinedExtent.xmax += 10;
            combinedExtent.xmin -= 10;
            combinedExtent.ymax += 10;
            combinedExtent.ymin -= 10;
          }

          if (!newExtent) {
            view.goTo(combinedExtent).then(response => {
              handleMapSelection(updatedFilter, mapPointCoords, newExtent, layers, map, queries);
            });
          }
          else {
            handleMapSelection(updatedFilter, mapPointCoords, newExtent, layers, map, queries);
          }
        }
        else {
          if (view.zoom >= 9) {
            map.layers.items[1].featureReduction = null;
          } 
          else {
            map.layers.items[1].featureReduction = _featureReduction;
          }
          if (!mapPointCoords) {
            noMapResultsElt.style.display = 'block';
          }
          if (mapPoint) {
            mapPointCoords = getMapPointCoords(mapPoint);
            for (let i = 0; i < layerViews.length; i++) {
              let layerViewFilter = prevFilters[i] !== null ? prevFilters[i] :
              {
                geometry: new _Point({
                  hasM: false,
                  hasZ: false,
                  latitude: mapPointCoords.lat,
                  longitude: mapPointCoords.long,
                  m: undefined,
                  spatialReference: view.spatialReference,
                  type: "point",
                  x: mapPointCoords.x,
                  y: mapPointCoords.y,
                  z: undefined
                }),
                distance: mapPointCoords ? (mapPointCoords.zoom < 8 ? 12 : mapPointCoords.zoom >= 8 && mapPointCoords.zoom < 10 ? 2 : mapPointCoords.zoom > 12 ? 0.005 : 0.5) : 1000,
                units: "miles",
                where: definitionExpression
              };

              layerViews[i].effect = {
                filter: layerViewFilter,
                excludedEffect: "grayscale(100%) opacity(20%)"
              };
            }
          }
          else {
            for (let i = 0; i < layers.length; i++) {
              layerViews[i].filter = definitionExpression ? {
                where: definitionExpression 
              }: {
                where: initDefExp
              };
              layerViews[i].effect = null;
            }
          }
        }
      });
    });
  }
}

function handleMapSelection(updatedFilter, mapPointCoords, newExtent, layers, map, queries) {
  if (updatedFilter === "addedMapPoint" || updatedFilter === "urlAddedMapPoint" || mapPointCoords || newExtent) {
    map.layers.items[1].featureReduction = null;
    
    _mapSelectionOccurred = true;
    let attributes = [];
    let selectedProjKeyNos = [];

    layers[0].queryFeatures(queries[0]).then(res3 => {
      for (let i = 0; i < res3.features.length; i++) {
        attributes.push(res3.features[i].attributes);
      }

      layers[1].queryFeatures(queries[1]).then(res4 => {
        for (let i = 0; i < res4.features.length; i++) {
          attributes.push(res4.features[i].attributes);
        }

        let uniqueAttributes = removeDuplicates(attributes, "FME_PROJ_KEY_NO");
        for (let i = 0; i < uniqueAttributes.length; i++) {
          selectedProjKeyNos.push(uniqueAttributes[i].FME_PROJ_KEY_NO);
        }

        let mapPointParam = mapPointCoords ? (mapPointCoords.lat + "," + mapPointCoords.long + "," + mapPointCoords.x + "," + mapPointCoords.y + "," + mapPointCoords.zoom) : "";

        if (newExtent && _postMapZoom) {
          _mapSelectionOccurred = false;
          _postMapZoom(selectedProjKeyNos, queries[0].geometry);
        }
        else if (_postMapSelection) {
          _postMapSelection(mapPointParam, selectedProjKeyNos, updatedFilter);
        }
      });
    });
  }
  else {
    _mapSelectionOccurred = false;
  }
}

export function updateProjectsList(view, layerViews) {
  let queries = [];
  let projKeyNos = [];
  for (let i = 0; i < layerViews.length; i++) {
    let query = layerViews[i].layer.createQuery();
    query.where = _definitionExpression ? _definitionExpression : null;
    query.geometry = view.extent;
    query.outFields = ["FME_PROJ_KEY_NO"];
    queries.push(query);
  }

  if (!_mapSelectionOccurred) {
    if (view.zoom > 6) {
      layerViews[0].queryFeatures(queries[0]).then(function(results) {
        if (results.features && results.features.length > 0) {
          for (let j = 0; j < results.features.length; j++) {
            if (!projKeyNos.includes(results.features[j].attributes.FME_PROJ_KEY_NO)) {
              projKeyNos.push(results.features[j].attributes.FME_PROJ_KEY_NO);
            }
          }
        }
        
        layerViews[1].queryFeatures(queries[1]).then(function(results2) {
          if (results2.features && results2.features.length > 0) {
            for (let j = 0; j < results2.features.length; j++) {
              if (!projKeyNos.includes(results2.features[j].attributes.FME_PROJ_KEY_NO)) {
                projKeyNos.push(results2.features[j].attributes.FME_PROJ_KEY_NO);
              }
            }
          }
          
          for (let i = 0; i < layerViews.length; i++) {
            let layerViewFilter = _definitionExpression ? {
              where: _definitionExpression,
              geometry: queries[i].geometry
            } : {
              where: null,
              geometry: queries[i].geometry
            };
    
            layerViews[i].effect = null;
            layerViews[i].filter = layerViewFilter;
          }

          _postMapZoom(projKeyNos, queries[0].geometry);
        });
      });
    }
    else {
      for (let i = 0; i < layerViews.length; i++) {
        let layerViewFilter = _definitionExpression ? {
          where: _definitionExpression
        } : {
          where: null
        };

        layerViews[i].effect = null;
        layerViews[i].filter = layerViewFilter;
      }
      
      _postMapZoom(projKeyNos, null);
    }
  }
}

export function initItemsOnMap(map, view, initDefExp, noMapResultsElt, mapLoadErrorElt) {
  let queries = [];
  let extents = [];
  let layers = view.map.layers.items;

  for (let i = 0; i < layers.length; i++) {
    layers[i].definitionExpression = initDefExp;
    let query = layers[i].createQuery();
    query.where = initDefExp;
    query.outSpatialReference = view.spatialReference;
    queries.push(query);
  }

  layers[0].queryExtent(queries[0]).then(res => {
    if (res.extent && res.count > 0) {
      extents.push(res.extent);
    }

    layers[1].queryExtent(queries[1]).then(res2 => {
      if (res2.extent && res2.count > 0) {
        extents.push(res2.extent);
      }

      if (extents.length) {
        noMapResultsElt.style.display = 'none';
        let newExtent = new _Extent(extents[0].xmin, extents[0].ymin, extents[0].xmax, extents[0].ymax, extents[0].spatialReference);
        for (let i = 1; i < extents.length; i++) {
          newExtent = newExtent.union(new _Extent(extents[i].xmin, extents[i].ymin, extents[i].xmax, extents[i].ymax, extents[i].spatialReference));
        }

        if (newExtent.xmax === newExtent.xmin && newExtent.ymax === newExtent.ymin) {
          newExtent.xmax += 10;
          newExtent.xmin -= 10;
          newExtent.ymax += 10;
          newExtent.ymin -= 10;
        }

        view.goTo(newExtent);
      }
      else {
        noMapResultsElt.style.display = 'block';
      }
    });
  });
}

// Updates map layers' definition expression based on changes to the projects being shown
export function updateItemsOnMap(map, view, layerViews, search, location, phase, performance, year, type, admin, mapPoint, mapped, newExtent, updatedFilter, noMapResultsElt, mapLoadErrorElt, postMapSelection, postMapZoom) {
  _mapPoint = mapPoint;
  _postMapSelection = postMapSelection;
  _postMapZoom = postMapZoom;
  
  if (updatedFilter !== "snapshot" && updatedFilter !== "mapPoint") {
    _definitionExpression = getDefinitionExpression(search, location, phase, performance, year, type, admin, mapped);
    _definitionExpression = _definitionExpression !== "" ? _definitionExpression : null;
    let mapPointCoords = getMapPointCoords(mapPoint);
    
    executeMapSelection(map, _definitionExpression, mapPointCoords, updatedFilter, view, layerViews, noMapResultsElt, mapPoint, newExtent);
  }
}

function getMapPointCoords(mapPoint) {
  let mapPointCoordsArr = mapPoint.split(",");
  let mapPointCoords = mapPointCoordsArr.length > 1 ? {
      lat: mapPointCoordsArr[0],
      long: mapPointCoordsArr[1],
      x: mapPointCoordsArr[2],
      y: mapPointCoordsArr[3],
      zoom: mapPointCoordsArr[4]
    } : null;

  return mapPointCoords;
}

// Creates definition expression based on URL search params
function getDefinitionExpression(search, location, phase, performance, year, type, admin, mapped) {
  let finalDefExp = "";
  let hasLocationProp = false;

  if (search.length) {
    finalDefExp += "(LOWER( FME_PROJ_NM ) LIKE '%" + search.toLowerCase().replace(/'/g, "''") + 
      "%' OR LOWER( FME_PROJ_KEY_NO ) LIKE '%" + search.toLowerCase().replace(/'/g, "''") +
      "%' OR LOWER( FME_CITY ) LIKE '%" + search.toLowerCase().replace(/'/g, "''") +
      "%' OR LOWER( FME_COUNTY ) LIKE '%" + search.toLowerCase().replace(/'/g, "''") + "%')" +
      " AND ";
  }

  for (var property in location) {
    if (location[property].selectedLocation.length) {
      hasLocationProp = true;
      if (location[property].selectedLocation.toLowerCase() === "bend") {
        finalDefExp += "(LOWER( " + location[property].attributeName + " ) LIKE 'bend' OR LOWER( " + location[property].attributeName + " ) LIKE 'bend,%' OR LOWER( " + location[property].attributeName + " ) LIKE '%,bend%')";
      }
      else {
        finalDefExp += "LOWER( " + location[property].attributeName + " ) LIKE '%" + location[property].selectedLocation.toLowerCase() + "%'";
      }

      finalDefExp += " OR ";
    }
  }
  finalDefExp = hasLocationProp ? finalDefExp.substring(0, finalDefExp.length - 4) + " AND " : finalDefExp;
  
  if (phase.length) {
    finalDefExp += "FME_PHASE IN (";
    for (let i = 0; i < phase.length; i++) {
      finalDefExp += "'" + phase[i] + "', ";
    }
    finalDefExp = finalDefExp.substring(0, finalDefExp.length-2) + ") AND ";
  }

  if (performance.length) {
    finalDefExp += "(";
    if (performance.includes("OnTime")) {
      finalDefExp += "Late = 'FALSE' OR ";
    }
    if (performance.includes("Late")) {
      finalDefExp += "Late = 'TRUE' OR ";
    }
    if (performance.includes("OnBudget")) {
      finalDefExp += "OverBudget = 'FALSE' OR ";
    }
    if (performance.includes("OverBudget")) {
      finalDefExp += "OverBudget = 'TRUE' OR ";
    }
    finalDefExp = finalDefExp.substring(0, finalDefExp.length - 4) + ") AND ";
  }
  if (year.length) {
    finalDefExp += "FME_SCHED_YR IN (";
    for (let i = 0; i < year.length; i++) {
      finalDefExp += "'" + year[i] + "', ";
    }
    finalDefExp = finalDefExp.substring(0, finalDefExp.length - 2) + ") AND ";
  }
  if (type.length) {
    finalDefExp += "(";
    for (let i = 0; i < type.length; i++) {
      finalDefExp += "(FME_PROJ_TYPE LIKE '%" + type[i] + "%') OR ";
    }
    finalDefExp = finalDefExp.substring(0, finalDefExp.length - 4) + ") AND ";
  }
  if (admin.length) {
    finalDefExp += "STIP_ADMINISTRATOR_ID IN (";
    for (let i = 0; i < admin.length; i++) {
      if (admin[i] === "LOCAL") {
        finalDefExp += "'LOCAL', 'LOCAL/CERT', 'LOCAL/COBO', 'LOCAL/SFLP', ";
      }
      else if (admin[i] === "ODOT") {
        finalDefExp += "'MAINT', 'ODOT', 'ODOT/LOCAL', 'RAIL', 'TRANSIT', ";
      }
      else {
        finalDefExp += "'" + admin[i] + "', ";
      }
    }

    finalDefExp = finalDefExp.substring(0, finalDefExp.length - 2) + ") AND ";
  }
  if (mapped.length) {
    finalDefExp += "FME_MAPPED IN (";
    for (let i = 0; i < mapped.length; i++) {
      finalDefExp += "'" + mapped[i] + "', ";
    }
    finalDefExp = finalDefExp.substring(0, finalDefExp.length - 2) + ") AND ";
  }

  if (finalDefExp.length) {
    finalDefExp = finalDefExp.substring(0, finalDefExp.length - 5);
  }
  
  return finalDefExp;
}